import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Spinner, Toast } from '../../lib';
import apiServices from '../../services/apiServices';
import { GetMySupportTickets, SupportTicket } from '../../services/dtos';
import {
  MemberStatusBadge,
  TicketCategoryBadge,
  TicketConfidentialBadge,
  fmtDateTime,
} from './supportShared';

const SupportMyTicketsPage: React.FC = () => {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiServices.client
      .get(new GetMySupportTickets())
      .then((data: SupportTicket[]) => {
        setTickets(data);
        setLoading(false);
      })
      .catch(() => {
        setError('Failed to load your requests');
        setLoading(false);
      });
  }, []);

  return (
    <div className="mx-auto w-full flex max-w-3xl flex-col gap-6 dark:text-white">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">My Support Requests</h2>
        <Button size="sm" onClick={() => navigate('/help/new')}>
          New Request
        </Button>
      </div>

      {error && <Toast color="failure">{error}</Toast>}
      {loading && <Spinner color="info" />}

      {!loading && !error && tickets.length === 0 && (
        <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-600 p-8 text-center text-sm text-gray-500 dark:text-gray-400">
          You haven't submitted any support requests yet.
          <div className="mt-3">
            <Button size="sm" onClick={() => navigate('/help/new')}>
              Ask a question
            </Button>
          </div>
        </div>
      )}

      {tickets.length > 0 && (
        <ul className="divide-y divide-gray-100 dark:divide-gray-700 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
          {tickets.map((t) => (
            <li key={t.id}>
              <Link
                to={`/help/ticket/${t.id}`}
                className="flex flex-col gap-1 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="font-medium text-gray-900 dark:text-white break-words">
                    {t.subject}
                  </span>
                  {/* T5: where the request stands (vision Feature 6) — not
                      the raw workflow status */}
                  <MemberStatusBadge ticket={t} />
                </div>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
                  <span>#{t.id}</span>
                  <TicketCategoryBadge category={t.category} />
                  <TicketConfidentialBadge isConfidential={t.isConfidential} />
                  <span>Opened {fmtDateTime(t.createdAtUtc)}</span>
                  <span>Last activity {fmtDateTime(t.lastActivityAtUtc)}</span>
                  {t.showSnapshot && <span>Show: {t.showSnapshot}</span>}
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default SupportMyTicketsPage;
